import { Injectable } from '@angular/core';
import { catchErrorTypedAsync } from '../../functions-helper.service';
import { WorkspaceAssetStateManagerService } from '../state-management/associations/workspace-asset-state-manager.service';
import { BaseActionService } from '../../../models/base-action-service';
import { IMoveAssets, IRenameAsset } from '../../http-models/asset/asset-http-request-model';
import { WorkspaceAssetApiManagerService } from '../api/managers/associations/workspace-asset-api-manager.service';
import { SingleOrArray } from '../../../models/defines';
import { UploaderService } from '../../../components/file-drop/uploader.service';
import { IUploader, UPLOADER_SERVICE_UNIQUE_IDS_ENUM } from '../../../constants/shared/uploader-service-unique-ids';
import { assetConverter } from '../../../models/assets/assets-converter';
import { IAssetFolderDeleteCommand } from '../api/associations/workspace-asset-request.types';

@Injectable()
export class WorkspaceAssetActionService extends BaseActionService implements IUploader {
    constructor(
        private workspaceAssetStateManager: WorkspaceAssetStateManagerService,
        private workspaceAssetApi: WorkspaceAssetApiManagerService,
        private uploaderService: UploaderService
    ) {
        super();
    }

    public readonly type = UPLOADER_SERVICE_UNIQUE_IDS_ENUM.ASSET;
    async removeAssetAsync(assetIds: SingleOrArray<string>, assetName: string) {
        return this.trashAsync(Array.isArray(assetIds) ? assetIds : [assetIds], assetName);
    }

    async trashAsync(assetIds: string[], assetName = 'assets') {
        this.setLoadingAction(true);
        const succeeded = await this.workspaceAssetStateManager.trashAsync(assetIds);
        this.setLoadingAction(false);
        if (!succeeded) {
            this._errorActionSubject.next(`Could not delete asset`);
            return false;
        }
        this._successMessageSubject.next(`🗑️ "${assetName}" Deleted"!`);
        return true;
    }

    async restoreAsync(assetIds: string[]) {
        const succeeded = await this.workspaceAssetStateManager.restoreAsync(assetIds);
        if (!succeeded) {
            this.onActionFailed('Could not restore assets');
            return false;
        }
        this._successMessageSubject.next('Assets restored');
        return true;
    }

    async permanentlyRemoveAsync(assetIds: string[]) {
        const succeeded = await this.workspaceAssetStateManager.permanentlyRemoveAsync(assetIds);
        if (!succeeded) {
            this.onActionFailed('Could not permanently delete assets');
            return false;
        }
        this._successMessageSubject.next('Assets permanently deleted');
        return true;
    }

    async removeFromProjectAsync(assetIds: string[]) {
        const succeeded = await this.workspaceAssetStateManager.removeFromProjectAsync(assetIds);
        if (!succeeded) {
            this.onActionFailed('Could not remove assets from project');
            return;
        }
        this._successMessageSubject.next('Assets removed from project');
    }

    async renameFolderAsync(assetId: string, newName: string) {
        const succeeded = await this.workspaceAssetStateManager.renameFolderAsync(assetId, newName);
        if (!succeeded) {
            this.onActionFailed(`Could not rename folder ${newName}`);
        }
    }

    async deleteFolderAsync(assetId: string, children: IAssetFolderDeleteCommand['children']) {
        const succeeded = await this.workspaceAssetStateManager.deleteFolderAsync(assetId, children);
        if (!succeeded) {
            this.onActionFailed('Could not delete folder');
            return false;
        }
        this.onActionSuccess('Folder deleted');
        return true;
    }

    async getAssetsByIdsAsync(assetIds: SingleOrArray<string>) {
        const { data, error } = await this.workspaceAssetApi.getAssetsByIdsAsync(assetIds);
        if (error) {
            this.onActionFailed(`Could not get generated asset. Please try again layer`);
            return null;
        }
        const localAssets = assetConverter.convertArrayOfInAssetsToLocal(data);
        return localAssets;
    }

    async renameFileAsync(body: IRenameAsset, oldName: string) {
        const { data, error } = await this.workspaceAssetApi.renameFileAssetAsync(body);
        if (error) {
            this.onActionFailed(`An error occurred when changing file name ${oldName}`);
            return;
        }
        this.onActionSuccess(`👌 "${oldName}" Renamed to "${body.newName}"!`);
    }

    async moveFileAsync(moveAssetPayload: IMoveAssets, displayName: string, fromFolder: string) {
        const { data, error } = await this.workspaceAssetApi.moveAssetsToFolderAsync(moveAssetPayload);
        if (error) {
            this.onActionFailed(
                `An error occurred when moving file ${displayName} from ${fromFolder} to ${moveAssetPayload.toFolder}`
            );
            return;
        }
        this.onActionSuccess(`✌ "${displayName}" Moved to ${moveAssetPayload.toFolder}!`);
    }

    async uploadAssetAsync(formData: FormData, folder: string, projectId?: string | null) {
        return await this.uploaderService.uploadFileAsync(
            this.type,
            this.workspaceAssetApi.uploadAssetAsync(formData, folder, projectId)
        );
    }

    async addToProjectAsync(projectId: string, assetIds: string[]) {
        const { error } = await this.workspaceAssetApi.addAssetsToProjectAsync({ projectId, assetIds });
        if (error) {
            this.onActionFailed('Could not add assets to project');
            return false;
        }
        this.onActionSuccess('Assets added to project');
        return true;
    }

    async createFolderAsync(folderName: string) {
        const { data, error } = await this.workspaceAssetApi.createFolderAsync(folderName);
        if (error) {
            this.onActionFailed(`An error occurred when creating ${folderName}`);
            return;
        }
        this.onActionSuccess(`👌 "${folderName}" Created!`);
    }

    // async renameFolderAsync(oldName: string, body: IRenameAsset) {
    //     const { data, error } = await this.workspaceAssetApi.renameFolder(body);
    //     if (error) {
    //         this.onActionFailed(`An error occurred when changing folder ${oldName} name `);
    //         return;
    //     }
    //     this.onActionSuccess(`👌 "${oldName}" Renamed to "${body.newName}"!`);
    // }

    private onActionSuccess(message: string) {
        this._successMessageSubject.next(message);
        this.workspaceAssetStateManager.loadAssetsAsync();
    }

    private onActionFailed(message: string) {
        this._errorActionSubject.next(message);
    }

    private setLoadingAction(isLoading: boolean) {
        if (typeof isLoading !== 'boolean') {
            return;
        }
        this._isLoadingActionSubject.next(isLoading);
    }
}
